import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ApartmentOutlined as SiteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  SectionLabel,
  stickyActionsSx,
  stickyTitleSx,
} from '../../../components/dialogs/formParts';
import { Project } from '../../../models/Project';
import { CreateSiteProjectRequest, UpdateSiteProjectRequest } from '../../../models/SiteProject';
import { SiteProjectService } from '../../../services/SiteProjectService';

interface CreateSiteDialogProps {
  open: boolean;
  initialProject?: Project | null;  // 전달 시 edit 모드
  onClose: () => void;
  onSaved: (message: string) => void;
}

type Status = 'active' | 'inactive' | 'maintenance';

const EMPTY_FORM: CreateSiteProjectRequest = {
  name: '',
  name_ko: '',
  location: '',
  address: '',
  description: '',
  status: 'active',
  dashboard_url: '',
  construction_completed_at: null,
  final_completed_at: null,
};

/**
 * ISO 문자열 ("2026-05-15T00:00:00Z") → HTML <input type="date"> 값 ("2026-05-15").
 * 빈/falsy 입력은 빈 문자열로 정규화.
 */
const toDateInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // YYYY-MM-DD in local TZ to match user's intuitive expectation.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * HTML <input type="date"> 값 ("2026-05-15") → 백엔드 ISO 8601 (UTC midnight).
 * 빈 문자열은 null 로 변환해 백엔드 필드를 변경 없음 / 명시적 클리어와 구분.
 */
const fromDateInput = (yyyymmdd: string): string | null => {
  if (!yyyymmdd) return null;
  return `${yyyymmdd}T00:00:00Z`;
};

const toForm = (p: Project | null | undefined): CreateSiteProjectRequest => {
  if (!p) return EMPTY_FORM;
  return {
    name: p.name,
    name_ko: p.name_ko || '',
    location: p.location,
    address: p.address || '',
    description: p.description || '',
    status: p.status ?? 'active',
    // BUG-FIX(2026-05-12): 기존엔 항상 '' 로 초기화 → edit 다이얼로그가 빈 URL 로 열리고
    // 사용자가 안 건드리고 저장하면 backend 가 빈 값으로 덮어씌웠음. 원본 값을 그대로 채워둠.
    dashboard_url: p.dashboard_url || '',
    construction_completed_at: p.construction_completed_at ?? null,
    final_completed_at: p.final_completed_at ?? null,
  };
};

/**
 * Site name validation rules.
 *
 * The site name doubles as the GitHub Actions `site` workflow input
 * (see .github/workflows/deploy.yaml). The workflow uses it directly
 * in docker compose service names (`${SITE}-router`, `${SITE}-map`, ...)
 * which are defined in docker/deploy/company/sites/<site>/docker-compose.yaml.
 * Therefore it MUST be:
 *   - lowercase ASCII letters, digits, and hyphens only
 *   - start with a letter
 *   - 2~30 characters
 *
 * Korean names are rejected at form level so operators don't discover
 * the constraint only after a failed batch deploy.
 */
const SITE_NAME_REGEX = /^[a-z][a-z0-9-]{1,29}$/;

function validateSiteName(raw: string): string | null {
  const name = raw.trim();
  if (name.length === 0) return '현장 이름을 입력해주세요.';
  if (/[A-Z]/.test(name)) return '대문자는 사용할 수 없습니다. 영문 소문자만 입력해주세요.';
  // Match any non-ASCII character (including 한글).
  // eslint-disable-next-line no-control-regex
  if (/[^\x00-\x7F]/.test(name)) return '한글·특수문자는 사용할 수 없습니다. 영문 소문자·숫자·하이픈(-)만 허용됩니다.';
  if (!SITE_NAME_REGEX.test(name)) {
    return '형식이 올바르지 않습니다. 영문 소문자로 시작하고, 소문자·숫자·하이픈(-)만 사용해주세요 (예: banpo, osong, central-1).';
  }
  return null;
}

const CreateSiteDialog: React.FC<CreateSiteDialogProps> = ({
  open,
  initialProject,
  onClose,
  onSaved,
}) => {
  const isEdit = !!initialProject;
  const theme = useTheme();
  // 업무 등록 폼과 같은 규칙 — 좁은 화면의 모달은 좌우 여백만 먹으므로 전체 화면.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState<CreateSiteProjectRequest>(toForm(initialProject));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(toForm(initialProject));
      setError(null);
      setNameTouched(false);
    }
  }, [open, initialProject]);

  const nameError = useMemo(() => validateSiteName(form.name), [form.name]);
  const showNameError = nameTouched && nameError !== null;

  const canSubmit =
    nameError === null && form.location.trim().length > 0 && !submitting;

  const handleNameChange = (raw: string) => {
    // Auto-normalize: strip spaces and lowercase ASCII letters as the user
    // types so they see immediately what will be sent. Non-ASCII chars
    // still fall through to the validator for an explicit rejection.
    const normalized = raw.replace(/\s+/g, '').toLowerCase();
    setForm({ ...form, name: normalized });
  };

  const handleSubmit = async () => {
    // Final guard — should already be blocked by canSubmit, but defense-in-depth.
    if (nameError) {
      setNameTouched(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && initialProject) {
        const payload: UpdateSiteProjectRequest = { ...form };
        await SiteProjectService.updateProject(parseInt(initialProject.id, 10), payload);
        onSaved('현장이 수정되었습니다');
      } else {
        await SiteProjectService.createProject(form);
        onSaved('현장이 등록되었습니다');
      }
      onClose();
    } catch (e) {
      console.error('현장 저장 실패:', e);
      setError('저장에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  // 업무 등록(ScheduleFormDialog)과 같은 포맷 — 아이콘+고정 제목, 섹션 라벨,
  // 모바일 전체 화면, 하단 고정 액션바. 폼이 화면마다 다르게 생기면 사용자가
  // "여긴 다른 규칙인가" 를 매번 다시 배워야 한다.
  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={stickyTitleSx}>
        <SiteIcon color="action" />
        <Box component="span" sx={{ flex: 1, fontSize: '1.05rem', fontWeight: 700 }}>
          {isEdit ? '현장 수정' : '현장 등록'}
        </Box>
        <IconButton onClick={onClose} disabled={submitting} aria-label="닫기" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Stack spacing={2.5}>
          <Alert severity="info">
            현장 이름은 <strong>GitHub Actions 배포 식별자</strong>로 그대로 사용됩니다.{' '}
            docker compose 서비스명(<code>{'{이름}-router, {이름}-map ...'}</code>)에 포함되므로{' '}
            <strong>영문 소문자·숫자·하이픈(-)</strong>만 허용됩니다. 예: <code>banpo</code>,{' '}
            <code>osong</code>, <code>central-1</code>.
          </Alert>

          {/* 1. 이름 */}
          <Box>
            <SectionLabel required>현장 이름 (영문, 배포에 사용)</SectionLabel>
            <TextField
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => setNameTouched(true)}
              error={showNameError}
              helperText={
                showNameError
                  ? nameError
                  : '영문 소문자로 시작, 소문자·숫자·하이픈(-)만 사용 (2~30자)'
              }
              fullWidth
              autoFocus={!isMobile}
              placeholder="예: banpo"
              inputProps={{
                'aria-label': '현장 이름 (영문, 배포에 사용)',
                maxLength: 30,
                // Semantic hint for password managers / autofill: never autofill.
                autoComplete: 'off',
                // Prevent IME (Korean/Japanese/Chinese composition) entirely.
                lang: 'en',
                pattern: '[a-z][a-z0-9-]{1,29}',
                spellCheck: false,
              }}
            />
          </Box>

          <Box>
            <SectionLabel>현장 이름 (한글, 표시용)</SectionLabel>
            <TextField
              value={form.name_ko ?? ''}
              onChange={(e) => setForm({ ...form, name_ko: e.target.value })}
              fullWidth
              placeholder="예: 반포 자이, 오송 그린파크"
              helperText="목록·카드에 표시되는 한글 이름. 비우면 영문 이름이 표시됩니다."
              inputProps={{ 'aria-label': '현장 이름 (한글, 표시용)' }}
            />
          </Box>

          {/* 2. 위치 */}
          <Box>
            <SectionLabel required>위치 (짧은 표시 라벨)</SectionLabel>
            <TextField
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              fullWidth
              placeholder="예: 서울 반포"
              helperText="현장 카드/리스트에 표시되는 짧은 라벨"
              inputProps={{ 'aria-label': '위치 (짧은 표시 라벨)' }}
            />
          </Box>

          <Box>
            <SectionLabel>주소 (지도 표시용, 선택)</SectionLabel>
            <TextField
              value={form.address ?? ''}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              fullWidth
              placeholder="예: 서울특별시 서초구 반포대로 1"
              helperText="정확한 주소를 입력하면 현장 설정에서 지도/길찾기 링크가 활성화됩니다"
              inputProps={{ 'aria-label': '주소 (지도 표시용, 선택)' }}
            />
          </Box>

          {/* 3. 설명 */}
          <Box>
            <SectionLabel>설명</SectionLabel>
            <TextField
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
              inputProps={{ 'aria-label': '설명' }}
            />
          </Box>

          <Box>
            <SectionLabel>대시보드 URL (선택)</SectionLabel>
            <TextField
              value={form.dashboard_url}
              onChange={(e) => setForm({ ...form, dashboard_url: e.target.value })}
              placeholder="예: https://grafana.example.com/d/abc123"
              fullWidth
              inputProps={{ 'aria-label': '대시보드 URL (선택)' }}
            />
          </Box>

          {/* 4. 날짜 — 업무 등록의 기간과 같은 배치(모바일 세로, 그 위 가로). */}
          <Box>
            <SectionLabel>준공 · 완공 날짜</SectionLabel>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="준공 (시공 완료)"
                type="date"
                value={toDateInput(form.construction_completed_at)}
                onChange={(e) =>
                  setForm({ ...form, construction_completed_at: fromDateInput(e.target.value) })
                }
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="완공 (최종 인수)"
                type="date"
                value={toDateInput(form.final_completed_at)}
                onChange={(e) =>
                  setForm({ ...form, final_completed_at: fromDateInput(e.target.value) })
                }
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>

          {/* 5. 상태 */}
          <Box>
            <SectionLabel required>상태</SectionLabel>
            <Select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as Status })}
              fullWidth
              inputProps={{ 'aria-label': '상태' }}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
            </Select>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={stickyActionsSx}>
        <Button onClick={onClose} disabled={submitting}>
          취소
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          sx={{ minWidth: 96 }}
        >
          {submitting ? '저장 중...' : isEdit ? '수정' : '등록'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSiteDialog;
